import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';

interface CaptainChatInputProps {
  disabled?: boolean;
  autoFocus?: boolean;
  onSend: (text: string) => void;
}

export interface CaptainChatInputHandle {
  focus: () => void;
}

const CaptainChatInput = forwardRef<CaptainChatInputHandle, CaptainChatInputProps>(
  ({ disabled, autoFocus = false, onSend }, ref) => {
    const [value, setValue] = React.useState('');
    const inputRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(ref, () => ({
      focus: () => inputRef.current?.focus(),
    }));

    useEffect(() => {
      if (autoFocus && !disabled) {
        inputRef.current?.focus();
      }
    }, [autoFocus, disabled]);

    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      const trimmed = value.trim();
      if (!trimmed || disabled) return;
      onSend(trimmed);
      setValue('');
    };

    const handleFocus = () => {
      // Keep the input visible above the iOS keyboard without triggering page zoom (16px font).
      requestAnimationFrame(() => {
        inputRef.current?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      });
    };

    return (
      <form className="vc-panel__chat-form" onSubmit={handleSubmit}>
        <input
          ref={inputRef}
          type="text"
          className="vc-panel__chat-input"
          placeholder="Ask Peppy…"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onFocus={handleFocus}
          disabled={disabled}
          aria-label="Message to Peppy AI"
          autoComplete="off"
          enterKeyHint="send"
        />
        <button type="submit" className="vc-panel__chat-send" disabled={disabled || !value.trim()}>
          Send
        </button>
      </form>
    );
  }
);

CaptainChatInput.displayName = 'CaptainChatInput';

export default CaptainChatInput;
